import { Card } from '@/components/ui/card';
import { ExternalLink, Check, DollarSign, Star } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ServiceProviders = ({ services, categoryTitle, categoryIcon: Icon, categoryColor }) => {
  const [ratingsData, setRatingsData] = useState({});

  useEffect(() => {
    // Fetch ratings for all providers
    const fetchRatings = async () => {
      const ratings = {};
      for (const service of services) {
        try {
          const response = await axios.get(`${API}/service-ratings/provider/${encodeURIComponent(service.name)}`);
          ratings[service.name] = response.data;
        } catch (error) {
          console.error(`Failed to fetch ratings for ${service.name}`);
        }
      }
      setRatingsData(ratings);
    };
    fetchRatings();
  }, [services]);

  const trackClick = async (service) => {
    try {
      await axios.post(`${API}/affiliate/track-click`, {
        provider_name: service.name,
        provider_category: categoryTitle,
        provider_website: service.website
      });
    } catch (error) {
      console.error('Failed to track click:', error);
    }
  };

  return (
    <Card className={`p-6 border-2 border-${categoryColor}-200`}>
      <div className="flex items-start space-x-4 mb-6">
        <div className={`w-14 h-14 bg-${categoryColor}-100 rounded-xl flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-8 h-8 text-${categoryColor}-600`} />
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{categoryTitle}</h3>
          <p className="text-sm text-gray-600">
            Trusted DFW service providers • Professional quality • Competitive pricing
          </p>
        </div>
      </div>

      <div className="space-y-4">
        {services.map((service, index) => (
          <div key={index} className={`bg-white border-2 rounded-lg p-4 hover:shadow-lg transition-all relative ${service.featured ? 'border-orange-500 shadow-md' : 'border-gray-200'}`}>
            {service.featured && (
              <div className="absolute -top-3 right-4 bg-orange-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                ⭐ FEATURED PARTNER
              </div>
            )}
            <div className="flex justify-between items-start mb-2">
              <div>
                <h4 className="font-bold text-lg text-gray-900">{service.name}</h4>
                {(service.rating || ratingsData[service.name]) && (
                  <div className="flex items-center gap-1 text-sm text-yellow-600">
                    <Star className="w-4 h-4 fill-yellow-400" />
                    <span className="font-semibold">
                      {ratingsData[service.name]?.average_rating || service.rating}
                    </span>
                    <span className="text-gray-500">
                      ({ratingsData[service.name]?.total_ratings || service.reviews || 0} reviews)
                    </span>
                  </div>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-700 mb-3">{service.description}</p>

            {service.highlights && service.highlights.length > 0 && (
              <div className="mb-3 space-y-1">
                {service.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span>{highlight}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between gap-4">
              <a
                href={service.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackClick(service)}
                className={`inline-flex items-center gap-2 font-bold px-6 py-3 rounded-lg transition-all transform hover:scale-105 ${
                  service.urgencyCTA 
                    ? 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white shadow-lg' 
                    : 'text-orange-600 hover:text-orange-700 font-medium text-sm'
                }`}
              >
                <span>{service.urgencyCTA || 'Visit Website'}</span>
                <ExternalLink className="w-4 h-4" />
              </a>
              {service.phone && (
                <a href={`tel:${service.phone}`} className="text-sm text-gray-600 hover:text-gray-900">
                  {service.phone}
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default ServiceProviders;
